/**
 * data-sorting-header.component.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { Component, HostListener, Input, OnInit } from "@angular/core";

import { SettingService } from "app/configuration/setting.service";
import { dot } from "../../support/utilities/functions";
import { DataType } from "./data-types";
import { TypedData, TypedDataHolder } from "./typed-data";
import { getTypeObject } from "app/models/data-types";

export enum SortState
{
    Unsorted = -1,
    Ascending = 0,
    Descending = 1
}
export type SortTarget = any[] & {_sort? : {dot : string, state : SortState}};

export const REVERSE_SORT_INDICATOR : string = "↑";

@Component({
    selector: `th[sorts], td[sorts], th>span[sorts]`,
    template: `
        <span
            class="glyphicon"
            [class.glyphicon-chevron-up]="state === 1"
            [class.glyphicon-chevron-down]="state === 0">
        </span>
        <span
            (click)="initiateSort()"
            class="dotted"
            title="Click to change the column sorting method">
            <ng-content></ng-content>
        </span>
    `,
    styles: [`.dotted {border-bottom: 1px dotted #555;}`]
})
export class DataSortingHeaderComponent implements OnInit
{
    @Input()
    public set sorts (target: any[]) {
        if (this._sorts !== target) {
            this._sorts = target;
            if (this.runOnChange) {
                this.trySort();
            }
        }
    }

    public _sorts : SortTarget;

    @Input()
    public dataProp : string;

    @Input()
    public dot : string;

    @Input()
    public asType : DataType;

    @Input()
    public after : {afterSort : () => void};

    @Input()
    public autorun : boolean = false;

    @Input()
    public runOnChange : boolean = false;

    @Input()
    public storedAs : string;

    public constructor (private settings : SettingService) {}

    public initiateSort () : void
    {
        this.trySort();
        if (this.storedAs)
        {
            if (this.state === SortState.Descending)
            {
                this.settings.set(
                    this.storedAs, REVERSE_SORT_INDICATOR + this.dot);
            }
            else
            {
                this.settings.set(this.storedAs, this.dot);
            }
        }
    }

    private trySort () : void
    {
        const state : SortState = this.state;
        if (state === SortState.Ascending)
        {
            this.sort(true);
            this.state = SortState.Descending;
        }
        else
        {
            this.sort();
            this.state = SortState.Ascending;
        }
    }

    public get state () : SortState
    {
        // NOTE: Don't do anything too complicated here -- since we use this
        // property in the view, it gets called a LOT. (Isn't much of a better
        // solution unless we switch to some sort of top-down approach where
        // we could store the value in some standard location...)
        if (this._sorts._sort && this._sorts._sort.dot === this.dot)
        {
            return +this._sorts._sort.state;
        }
        return SortState.Unsorted;
    }
    public set state (value : SortState)
    {
        this._sorts._sort = {dot : this.dot, state : value};
    }

    public ngOnInit () : void
    {
        if (!Array.isArray(this._sorts)
            || (this._sorts.length > 0 && (typeof this._sorts[0] !== "object")))
        {
            console.error(this._sorts);
            throw new Error(`Must point 'sorts' attribute at object array!`);
        }

        let runNow : boolean = this.autorun === true;
        if (this.storedAs)
        {
            const savedSortColumn : string =
                this.settings.getOr(this.storedAs, "");
            console.warn(`column sort for ${this.dot} =? ${savedSortColumn} (${(savedSortColumn === this.dot)} ${(savedSortColumn === REVERSE_SORT_INDICATOR + this.dot)})`);
            if (savedSortColumn === this.dot)
            {
                runNow = true;
            }
            if (savedSortColumn === REVERSE_SORT_INDICATOR + this.dot)
            {
                runNow = true;
                this.state = SortState.Ascending;
                // pretend we are already ascending so that trySort flips it
            }
        }
        if (runNow)
        {
            this.trySort();
        }
    }

    private access (host : any) : any
    {
        return dot(host, this.dot);
    }

    /**
     * Actually run the sorting function on the target data.
     * @param reverse boolean Whether to return the reversed order
     */
    private sort (reverse : boolean = false) : void
    {
        this._sorts.sort((a : any, b : any) => {
            let aData = this.access(a);
            let bData = this.access(b);

            if (this.asType)
            {
                aData = {value: aData, type: this.asType};
                bData = {value: bData, type: this.asType};
            } else {
                if (!TypedData.hasProperties(aData)) {
                    aData = {value: aData, type: TypedData.guessType(aData)};
                }
                if (!TypedData.hasProperties(bData)) {
                    bData = {value: bData, type: TypedData.guessType(bData)};
                }
            }
            const delta : number = TypedData.compare(aData, bData);
            return reverse ? -delta : delta;
        });
        if (this.after)
        {
            this.after.afterSort();
        }
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/common/data/data-sorting-header.component.ts